import styled from "styled-components";
import { useEffect, useRef, useState } from "react";
import moment from "moment";
import {
    CheckBoxComponent,
    StyledChecker,
    StyledButton,
    ClipLoaderCommon,
    RewardSlider,
    StyledBorderButton,
    CustomCheckbox,
} from "@components";
import {
    breakpoint,
    theme,
    useDynamicBottomHeight,
    pushBeginCheckout,
    ssStorage,
} from "@utilities";
import { ICartItemData, IPointsData, ICustomerDetail } from "@interfaces";
import { IPaymentReceiver } from "src/Interfaces/paymentReceiver";
import { ErrorToast } from "src/Components/toasts/ErrorToast";
import { Link, useLocation } from "react-router-dom";
import { useCheckout } from "../CheckoutContext";
import { calcTotalUsablePoints, formatMaxOneDecimal } from "../pointsHelpers";
import { getPointsPreviewMode, applyPointsPreview } from "./pointsPreview";
import { selectAccount, useAppSelector } from "@redux";
import { pointsApi, quoteApi } from "@api";
import { SendPaymentLinkModal } from "./SendPaymentLinkModal";
import { ViewSentEmailsModal } from "./ViewSentEmailsModal";
import { PointsSlider } from "./PointsSlider";
import { CommissionSlider } from "./CommissionSlider";
import { formatPrice } from "src/Utilities/priceHelper";

/** Height of the fixed header, plus breathing room above the summary heading. */
const ORDER_SUMMARY_SCROLL_OFFSET = 140;

interface IPaymentOptionProps {
    handleOrderCreate: (
        paymentType: string,
        pointsApplied: number,
        commissionApplied?: number | null,
    ) => Promise<void>;
    customers?: ICustomerDetail[];
    quoteId?: string;
    onOrderCreateSuccess?: () => void;
    isQuotesPage?: boolean;
    items?: ICartItemData[];
    embedded?: boolean;
}

export const PaymentOptionComponent = (props: IPaymentOptionProps): JSX.Element => {
    const { handleOrderCreate, customers = [], quoteId, onOrderCreateSuccess } = props;
    const [showHelp, setShowHelp] = useState(false);
    const [showSendLinkModal, setShowSendLinkModal] = useState(false);
    const [showViewSentEmailsModal, setShowViewSentEmailsModal] = useState(false);
    const [selectedTraveler, setSelectedTraveler] = useState<IPaymentReceiver | null>(null);
    const [isEmailing, setIsEmailing] = useState(false);
    const [isAgreeTerm, setIsAgreeTerm] = useState<boolean>(false);
    const [isOrderInProgress, setOrderInProgress] = useState<boolean>(false);
    const location = useLocation();
    const orderSummaryRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        // Land on the summary rather than the top of the page. Deliberately on
        // mount and ungated: waiting for the points lookups meant the card often
        // wasn't mounted yet when they settled, and the effect never re-ran, so
        // nothing scrolled at all. Header clearance comes from scrollMarginTop on
        // the card, which keeps the heading visible rather than under the header.
        orderSummaryRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, []);
    // Rewards are applied by default, with the slider seeded at its maximum.
    const [useRewardPoints, setUseRewardPoints] = useState(true);
    const [adjustSellPrice, setAdjustSellPrice] = useState(false);
    const { account } = useAppSelector(selectAccount);
    const [pointsToUse, setPointsToUse] = useState(0);
    const [commissionAdjustment, setCommissionAdjustment] = useState(0);
    const [isCommissionAtMax, setIsCommissionAtMax] = useState(false);
    const [isPointsLoading, setIsPointsLoading] = useState(false);
    const [fetchedPointsData, setPointsData] = useState<IPointsData>({
        subSystemType: "",
        pointsBalance: 0,
        availableBalance: 0,
        pointsMultiplier: 0,
    });

    const [isEditingPrice, setIsEditingPrice] = useState(false);
    const [editPriceValue, setEditPriceValue] = useState("");

    const handleUseRewardPoints = () => {
        setUseRewardPoints((prev) => !prev);
        if (useRewardPoints) {
            setPointsToUse(0);
        }
    };

    const handleAdjustSellPrice = () => {
        setAdjustSellPrice((prev) => !prev);
    };

    const handleEditPrice = {
        start: () => {
            setEditPriceValue(finalPrice.toFixed(2));
            setIsEditingPrice(true);
        },
        submit: () => {
            const newSellPrice = parseFloat(editPriceValue);
            if (!isNaN(newSellPrice) && newSellPrice > 0) {
                const newCommission = Math.max(
                    0,
                    Math.min(newSellPrice - (originalPrice - pointsDiscount), commission),
                );
                setCommissionAdjustment((commission - newCommission) * pointsData.pointsMultiplier);
            }
            setIsEditingPrice(false);
        },
        cancel: () => {
            setIsEditingPrice(false);
            setEditPriceValue("");
        },
    };

    const handleSelectTraveler = (traveler: IPaymentReceiver | null) => {
        setSelectedTraveler(traveler);
    };

    const handleSendLink = async () => {
        if (!selectedTraveler) {
            ErrorToast({ Message: "Select a traveler" });
            return;
        }
        if (!quoteId) {
            ErrorToast({ Message: "Quote ID is missing" });
            return;
        }
        try {
            const commissionToApply =
                isCommissionAgent && adjustSellPrice ? appliedCommission : null;
            const emailPayload = {
                quoteId: String(quoteId),
                pointsApplied: pointsToUse || null,
                commissionApplied: commissionToApply,
                ...(selectedTraveler.redeemer_id
                    ? { redeemerId: selectedTraveler.redeemer_id }
                    : { email: selectedTraveler.email, name: selectedTraveler.name, isNew: true }),
            };
            await quoteApi.sendPaymentEmails(emailPayload);
        } catch (error: any) {
            console.error(error);
            ErrorToast({ Message: error?.response?.data?.title || "Failed to send email" });
        } finally {
            setOrderInProgress(false);
        }
    };

    const handleResend = async (sharePaymentId: number) => {
        if (!quoteId) {
            ErrorToast({ Message: "Quote ID is missing" });
            return;
        }
        try {
            setIsEmailing(true);
            await quoteApi.resendPaymentLink({
                quoteId: String(quoteId),
                sharePaymentId,
            });
        } catch (error: any) {
            console.error(error);
            ErrorToast({ Message: "Failed to resend email" });
        } finally {
            setIsEmailing(false);
        }
    };

    const isPickupTimeInPast = (displayName: string, bookingDate: string): boolean => {
        // Try 12-hour format first (e.g., "08:30 AM Location")
        let timeMatch = displayName.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);

        if (timeMatch) {
            const [, hours, minutes, period] = timeMatch;
            let hour = parseInt(hours, 10);
            if (period.toUpperCase() === "PM" && hour !== 12) hour += 12;
            if (period.toUpperCase() === "AM" && hour === 12) hour = 0;
            const pickupDateTime = moment(bookingDate).hour(hour).minute(parseInt(minutes, 10));
            return pickupDateTime.isBefore(moment());
        }

        // Try 24-hour format (e.g., "06:30 Location" or "14:30 Location")
        timeMatch = displayName.match(/^(\d{1,2}):(\d{2})/);
        if (timeMatch) {
            const [, hours, minutes] = timeMatch;
            const hour = parseInt(hours, 10);
            const minute = parseInt(minutes, 10);
            const pickupDateTime = moment(bookingDate).hour(hour).minute(minute);
            return pickupDateTime.isBefore(moment());
        }

        return false;
    };

    const validatePickupTimes = (): boolean => {
        if (!props.items) return true;

        for (const item of props.items) {
            for (const bd of item.booking_data) {
                const pickupLocation = bd.pickupLocation;
                if (pickupLocation && isPickupTimeInPast(pickupLocation, item.booking_date)) {
                    ErrorToast({
                        Message:
                            "Cannot proceed: One or more pickup times are in the past. Please update your booking details.",
                    });
                    return false;
                }
            }
        }
        return true;
    };

    const handleCreateOrder = async (paymentType: string, pointsApplied: number) => {
        // GA4 begin_checkout when user clicks main payment CTA
        if (paymentType === "pay-now" && props.items && props.items.length > 0) {
            const firstItem = props.items[0];
            const productJson = firstItem.product?.json;
            if (productJson) {
                const travelDate = moment(firstItem.booking_date, "DD-MMM-YYYY").format(
                    "YYYY-MM-DD",
                );
                const categories = (productJson.categories ?? {}) as any;
                const snapshot = {
                    productId: productJson.productId,
                    name: productJson.name,
                    supplier: productJson.supplierName ?? productJson.tradingName ?? null,
                    categories: {
                        // Use *_Name / *Names fields where available so item_category2 shows names, not IDs.
                        locationNames: (
                            categories.locationNames ?? [
                                categories.startLocationName,
                                categories.endLocationName,
                            ]
                        ).filter(Boolean),
                        activityNames: categories.activityNames ?? [],
                        accommodationNames:
                            categories.accommodationNames ?? categories.accommodationName ?? [],
                        transportNames: categories.transportNames ?? categories.transportName ?? [],
                    },
                    tourCode: productJson.tourCode,
                    location: categories.startLocationName || categories.endLocationName,
                    duration:
                        productJson.durationDays && productJson.durationNight
                            ? `${productJson.durationDays} days - ${productJson.durationNight} night`
                            : undefined,
                };
                // Persist snapshot so we can emit purchase on successful completion (after redirect).
                ssStorage("gtmLastCheckout").setItem({
                    product: snapshot,
                    value: finalPrice,
                    travelDate,
                });
                pushBeginCheckout(snapshot, {
                    value: finalPrice,
                    travelDate,
                    ctaClick: "Proceed to Payment",
                });
            }
        }

        if (!validatePickupTimes()) {
            return;
        }

        try {
            setOrderInProgress(true);
            const commissionToApply =
                isCommissionAgent && adjustSellPrice ? appliedCommission : null;
            await handleOrderCreate(paymentType, pointsApplied, commissionToApply);
            onOrderCreateSuccess?.();
        } catch (error) {
            console.error(error);
            ErrorToast({ Message: "Failed to create order" });
        } finally {
            setOrderInProgress(false);
        }
    };

    const handleToggleAgree = () => {
        setIsAgreeTerm(!isAgreeTerm);
    };

    useEffect(() => {
        if (!account?.is_agent_integrated) return;
        setIsPointsLoading(true);
        pointsApi
            .getPointsData()
            .then((res) => {
                setPointsData(res.data.data);
            })
            .finally(() => {
                setIsPointsLoading(false);
            });
    }, [account?.is_agent_integrated]);

    useEffect(() => {
        if (account?.is_agent_integrated) {
            setIsAgreeTerm(true);
        }
    }, [account?.is_agent_integrated]);

    const {
        activeDiscount,
        orderDiscount,
        isOrderDiscountLoading,
        discountedPrice,
        originalPrice,
        pointsAvailable: fetchedPointsAvailable,
        verifyPoints,
        isVerifyingPoints,
        isUpgradingPoints,
    } = useCheckout();

    const hasVerifiedPointsRef = useRef(false);
    useEffect(() => {
        // Deliberately the real value, not the previewed one: this triggers a
        // server-side verification and must not fire on spoofed balances.
        if (
            account?.is_agent_integrated &&
            fetchedPointsAvailable !== undefined &&
            !hasVerifiedPointsRef.current
        ) {
            hasVerifiedPointsRef.current = true;
            verifyPoints();
        }
    }, [account?.is_agent_integrated, fetchedPointsAvailable, verifyPoints]);

    const { metadata } = useAppSelector(selectAccount);
    // ?pointsPreview=points|commission spoofs the agent flags and balances so the
    // points sections below can be worked on without a points agent account.
    // Non-production only; see pointsPreview.ts. Remove with that file.
    const pointsPreviewMode = getPointsPreviewMode(location.search);
    const {
        isAgent,
        isPointsAgent,
        isCommissionAgent,
        isPointsDisplayed,
        pointsData,
        pointsAvailable,
    } = applyPointsPreview(
        {
            isAgent: account?.is_agent_integrated,
            isPointsAgent: metadata?.is_points_agent ?? false,
            isCommissionAgent: metadata?.is_commission_agent ?? false,
            isPointsDisplayed: account?.is_points_displayed,
            pointsData: fetchedPointsData,
            pointsAvailable: fetchedPointsAvailable,
        },
        pointsPreviewMode,
    );
    const totalUsablePoints = calcTotalUsablePoints(pointsData?.availableBalance, pointsAvailable);
    const hasPoints = totalUsablePoints > 0;
    const totalPoints = formatMaxOneDecimal(totalUsablePoints);

    // An in-flight points upgrade means these figures are about to change, so the
    // summary shows its loading state rather than totals that look settled.
    const isTotalsPending = isOrderDiscountLoading || isUpgradingPoints;
    const showDiscountBadge = !isAgent && (activeDiscount ?? 0) > 0 && (orderDiscount ?? 0) > 0;
    const pointsDiscount = useRewardPoints ? pointsToUse / pointsData.pointsMultiplier : 0;

    const commission =
        pointsData?.pointsMultiplier && pointsAvailable
            ? pointsAvailable / pointsData.pointsMultiplier
            : 0;
    const maxCommissionDollar = commission;

    // Commission adjustment: how much commission to reduce from the original commission
    const commissionReductionDollar = adjustSellPrice
        ? Math.round((commissionAdjustment / pointsData.pointsMultiplier) * 100) / 100
        : 0;
    const adjustedCommission = commission - commissionReductionDollar;
    const appliedCommission = commissionReductionDollar;

    // For commission agents: originalPrice already includes max commission, so subtract the commission reduction
    // For points agents: originalPrice - pointsDiscount (no commission adjustment)
    // For normal users: discountedPrice or originalPrice
    const finalPrice = isAgent
        ? isCommissionAgent
            ? originalPrice - pointsDiscount - (adjustSellPrice ? appliedCommission : 0)
            : originalPrice - pointsDiscount
        : (discountedPrice ?? originalPrice);
    const hasDiscount = !!discountedPrice && originalPrice > discountedPrice;

    const bottomContainerRef = useDynamicBottomHeight(
        [props.isQuotesPage, account?.is_agent_integrated, isAgent],
        50,
    );

    return (
        <>
            {isCommissionAgent && isPointsDisplayed && (
                <CommissionBanner>
                    Your Commission will be paid to your bank account after travel date passes.
                </CommissionBanner>
            )}
            <Container $embedded={props.embedded}>
                {showHelp && (
                    <div className="help-popup-overlay" onClick={() => setShowHelp(false)}>
                        <div className="help-popup-content" onClick={(e) => e.stopPropagation()}>
                            <p>
                                The final discount shown is based on the items in your cart that are
                                eligible for the offer. Some products may not qualify, while others
                                might have reduced discounts due to operator limitations. That’s why
                                the final discount may be lower than expected — it’s automatically
                                adjusted based on eligible items only.
                            </p>
                            <StyledButton
                                $borderRadius="4px"
                                color={theme.gradients.button}
                                onClick={() => setShowHelp(false)}
                            >
                                Got it
                            </StyledButton>
                        </div>
                    </div>
                )}

                {showSendLinkModal && (
                    <SendPaymentLinkModal
                        customers={customers}
                        quoteId={quoteId}
                        selectedTraveler={selectedTraveler}
                        setSelectedTraveler={handleSelectTraveler}
                        isEmailing={isEmailing}
                        setIsEmailing={setIsEmailing}
                        isOrderInProgress={isOrderInProgress}
                        handleSendLink={handleSendLink}
                        handleResend={handleResend}
                        onClose={() => setShowSendLinkModal(false)}
                    />
                )}

                {showViewSentEmailsModal && (
                    <ViewSentEmailsModal
                        quoteId={quoteId}
                        handleResend={handleResend}
                        onClose={() => setShowViewSentEmailsModal(false)}
                    />
                )}

                <PriceBlockStyled>
                    <div className="inner-wrapper">
                        <div
                            className="summary-card"
                            ref={orderSummaryRef}
                            style={{ scrollMarginTop: ORDER_SUMMARY_SCROLL_OFFSET }}
                        >
                            <h2 className="summary-title">
                                Order Summary
                                <StyledItemInfo>
                                    {props.items?.length || 0}{" "}
                                    {props.items?.length === 1 ? "item" : "items"}
                                </StyledItemInfo>
                            </h2>

                            <div className="original-price-value-wrapper">
                                <div className="price-label">Total</div>
                                {isTotalsPending ? (
                                    <div className="price-original">
                                        <ClipLoaderCommon
                                            color={theme.colors.main}
                                            size={20}
                                            margin={0}
                                        />
                                    </div>
                                ) : (
                                    <div className="price-original">
                                        {formatPrice(originalPrice)}
                                    </div>
                                )}
                            </div>

                            {/* Show discount badge for normal users */}
                            {showDiscountBadge && (
                                <div className="discount-row">
                                    {isTotalsPending ? (
                                        <div className="loading-spinner"></div>
                                    ) : (
                                        <>
                                            <span className="discount-badge">
                                                {orderDiscount}% off
                                            </span>
                                            <QuestionMarkHelper>
                                                <img
                                                    onClick={() => setShowHelp(true)}
                                                    src="/images/HelpIcon.svg"
                                                    alt="info"
                                                />
                                            </QuestionMarkHelper>
                                        </>
                                    )}
                                </div>
                            )}

                            {/* Show adjust sell price for commission agents only when logged in */}
                            {isCommissionAgent && isPointsDisplayed && (
                                <>
                                    <div className="checkbox-option-commission">
                                        <CommissionSlider
                                            maxCommissionDollar={maxCommissionDollar}
                                            pointsMultiplier={pointsData.pointsMultiplier}
                                            value={commissionAdjustment}
                                            onChange={(value) => setCommissionAdjustment(value)}
                                            onMaxReached={(isMax) => setIsCommissionAtMax(isMax)}
                                            isLoading={commission === 0}
                                            onCheckboxChange={handleAdjustSellPrice}
                                            isChecked={adjustSellPrice}
                                            isCheckboxDisabled={isOrderDiscountLoading}
                                        />
                                    </div>
                                </>
                            )}
                            {/* Show points checkbox for agents */}
                            {isAgent &&
                                account?.is_points_displayed &&
                                (isPointsAgent || (isCommissionAgent && hasPoints)) && (
                                    <>
                                        <div className="checkbox-option">
                                            <div className="checkbox-row">
                                                <CustomCheckbox
                                                    isChecked={useRewardPoints}
                                                    onClick={handleUseRewardPoints}
                                                    disabled={
                                                        isOrderDiscountLoading ||
                                                        isPointsLoading ||
                                                        isVerifyingPoints
                                                    }
                                                />
                                                {isPointsAgent ? (
                                                    <p>
                                                        Use Reward points{" "}
                                                        {!useRewardPoints && (
                                                            <span className="price-discount-badge-wrapper">
                                                                {isPointsLoading ||
                                                                isVerifyingPoints ? (
                                                                    <div className="loading-spinner"></div>
                                                                ) : (
                                                                    <span className="price-discount-badge">
                                                                        {totalPoints} pts
                                                                    </span>
                                                                )}
                                                            </span>
                                                        )}
                                                    </p>
                                                ) : (
                                                    <span className="checkbox-label">
                                                        Use Reward points
                                                    </span>
                                                )}
                                            </div>
                                            {useRewardPoints &&
                                                (isPointsAgent ? (
                                                    <RewardSlider
                                                        earnedPoints={Math.max(
                                                            0,
                                                            pointsAvailable ?? 0,
                                                        )}
                                                        unUsedPoints={pointsData.availableBalance}
                                                        totalPoints={pointsData.pointsBalance}
                                                        pointsPerDollar={
                                                            pointsData.pointsMultiplier
                                                        }
                                                        productValue={originalPrice}
                                                        onChange={(pts) => setPointsToUse(pts)}
                                                        finalPrice={finalPrice}
                                                        originalPrice={originalPrice}
                                                    />
                                                ) : (
                                                    <>
                                                        <div className="points-summary-row">
                                                            <div className="points-summary-item">
                                                                <span className="points-summary-label">
                                                                    Total
                                                                </span>
                                                                <span className="points-summary-value">
                                                                    {formatMaxOneDecimal(
                                                                        pointsData.pointsBalance,
                                                                    )}{" "}
                                                                    pts
                                                                </span>
                                                            </div>
                                                            <div className="points-summary-item">
                                                                <span className="points-summary-label">
                                                                    Available
                                                                </span>
                                                                <span className="points-summary-value available">
                                                                    {formatMaxOneDecimal(
                                                                        pointsData.availableBalance,
                                                                    )}{" "}
                                                                    pts
                                                                </span>
                                                            </div>
                                                            <div className="points-summary-item">
                                                                <span className="points-summary-label">
                                                                    Pending
                                                                </span>
                                                                <span className="points-summary-value pending">
                                                                    {formatMaxOneDecimal(
                                                                        pointsData.pointsBalance -
                                                                            pointsData.availableBalance,
                                                                    )}{" "}
                                                                    pts
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="slider-container">
                                                            <PointsSlider
                                                                maxPoints={totalUsablePoints}
                                                                pointsPerDollar={
                                                                    pointsData.pointsMultiplier
                                                                }
                                                                productValue={originalPrice}
                                                                onChange={(pts) =>
                                                                    setPointsToUse(pts)
                                                                }
                                                            />
                                                        </div>
                                                    </>
                                                ))}
                                        </div>

                                        {adjustSellPrice &&
                                            account?.is_points_displayed &&
                                            useRewardPoints &&
                                            (isPointsAgent || isCommissionAgent) && (
                                                <div className="commission-info-row">
                                                    <span className="info-label">
                                                        Points value on Slider
                                                    </span>
                                                    <span className="info-value">
                                                        {formatMaxOneDecimal(pointsToUse)} pts
                                                        {/* Omitted rather than shown
                                                            as NaN: a zero or absent
                                                            multiplier makes this
                                                            division non-finite. */}
                                                        {isCommissionAgent &&
                                                            formatPrice(
                                                                pointsToUse /
                                                                    pointsData.pointsMultiplier,
                                                            ) &&
                                                            ` (-${formatPrice(
                                                                pointsToUse /
                                                                    pointsData.pointsMultiplier,
                                                            )})`}
                                                    </span>
                                                </div>
                                            )}
                                    </>
                                )}

                            {/* Commission info sections - outside of points section */}
                            {adjustSellPrice && account?.is_points_displayed && (
                                <div className="commission-info-row">
                                    <span className="info-label">Your new Commission will be</span>
                                    <span className="info-value">
                                        ${formatMaxOneDecimal(adjustedCommission)}
                                    </span>
                                </div>
                            )}

                            {adjustSellPrice && account?.is_points_displayed && (
                                <div className="price-value-wrapper">
                                    <div className="price-label-pay">New Sell Price</div>
                                    <div className="price-discounted-value">
                                        {(hasDiscount && !isAgent) ||
                                        (isPointsAgent && useRewardPoints && pointsToUse > 0) ? (
                                            <span className="price-original-strikethrough">
                                                {formatPrice(originalPrice)}
                                            </span>
                                        ) : null}
                                        <EditablePriceBox data-max={isCommissionAtMax}>
                                            {isEditingPrice ? (
                                                <>
                                                    <span>$</span>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        value={editPriceValue}
                                                        onChange={(e) =>
                                                            setEditPriceValue(e.target.value)
                                                        }
                                                        onBlur={handleEditPrice.submit}
                                                        onKeyDown={(e) => {
                                                            if (e.key === "Enter")
                                                                handleEditPrice.submit();
                                                            if (e.key === "Escape")
                                                                handleEditPrice.cancel();
                                                        }}
                                                        autoFocus
                                                    />
                                                </>
                                            ) : (
                                                <>
                                                    <span>{formatPrice(finalPrice)}</span>
                                                    <img
                                                        src="/images/Pencil.svg"
                                                        alt="edit"
                                                        width={14}
                                                        height={14}
                                                        onClick={handleEditPrice.start}
                                                        style={{ cursor: "pointer" }}
                                                    />
                                                </>
                                            )}
                                        </EditablePriceBox>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="action-section">
                            <p className="terms-text">
                                By proceeding with this booking the{" "}
                                <Link
                                    to="/booking-terms-and-conditions"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    booking terms and conditions
                                </Link>{" "}
                                have been agreed to.
                            </p>
                            {props.isQuotesPage && account?.is_agent_integrated && (
                                <ViewSentEmailsButton
                                    onClick={() => setShowViewSentEmailsModal(true)}
                                >
                                    <span>View sent links</span>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                        <path
                                            d="M9 18L15 12L9 6"
                                            stroke={theme.colors.highlight}
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </ViewSentEmailsButton>
                            )}
                            {props.isQuotesPage && account?.is_agent_integrated && (
                                <SendLinkButton
                                    $borderRadius="4px"
                                    $textColor={theme.colors.highlight}
                                    $borderColor={theme.colors.highlight}
                                    onClick={() => setShowSendLinkModal(true)}
                                    disabled={isOrderInProgress}
                                    data-gtm-id="send-payment-link"
                                >
                                    <div>
                                        <p>Send Payment Link</p>
                                    </div>
                                </SendLinkButton>
                            )}
                            <PaymentButton
                                $borderRadius="4px"
                                color={theme.gradients.button}
                                onClick={() => handleCreateOrder("pay-now", pointsToUse)}
                                disabled={isOrderInProgress || isTotalsPending}
                                data-gtm-id="begin-checkout"
                            >
                                {isTotalsPending ? (
                                    <ApplyingDiscount>
                                        <span>Applying Discount</span>
                                        <ClipLoaderCommon
                                            color={theme.colors.main}
                                            size={22}
                                            margin={0}
                                        />
                                    </ApplyingDiscount>
                                ) : isOrderInProgress ? (
                                    <ClipLoaderCommon
                                        color={theme.colors.main}
                                        size={35}
                                        margin={5}
                                    />
                                ) : (
                                    <div data-gtm-id="proceed-to-payment">
                                        <img src="/images/CardPay.svg" alt="cardPay" />
                                        <p>Proceed to Payment</p>
                                    </div>
                                )}
                            </PaymentButton>
                        </div>
                    </div>
                </PriceBlockStyled>
                <BottomContainer ref={bottomContainerRef} $embedded={props.embedded}>
                    <ListOptions>
                        <div className="checkBox">
                            {!isAgent && (
                                <>
                                    <StyledChecker>
                                        <CheckBoxComponent
                                            id="check"
                                            isChecked={isAgreeTerm}
                                            onChange={handleToggleAgree}
                                        />
                                    </StyledChecker>
                                    <p className="checkBoxText">
                                        I accept the{" "}
                                        <Link
                                            to="/booking-terms-and-conditions"
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            booking terms and conditions
                                        </Link>
                                    </p>
                                </>
                            )}
                            {isAgent && (
                                <p className="checkBoxText">
                                    By proceeding with this booking the{" "}
                                    <Link
                                        to="/booking-terms-and-conditions"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        booking terms and conditions
                                    </Link>{" "}
                                    have been agreed to.
                                </p>
                            )}
                        </div>
                    </ListOptions>
                    {props.isQuotesPage && account?.is_agent_integrated && (
                        <ViewSentEmailsButton onClick={() => setShowViewSentEmailsModal(true)}>
                            <span>View sent links</span>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M9 18L15 12L9 6"
                                    stroke={theme.colors.highlight}
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </ViewSentEmailsButton>
                    )}
                    {props.isQuotesPage && account?.is_agent_integrated && (
                        <SendLinkButton
                            $borderRadius="4px"
                            $textColor={theme.colors.highlight}
                            $borderColor={theme.colors.highlight}
                            onClick={() => setShowSendLinkModal(true)}
                            disabled={isOrderInProgress}
                            data-gtm-id="send-payment-link"
                        >
                            <div>
                                <p>Send Payment Link</p>
                            </div>
                        </SendLinkButton>
                    )}
                    <PaymentButton
                        $borderRadius={theme.borderRadius.small}
                        color={theme.gradients.button}
                        onClick={() => handleCreateOrder("pay-now", pointsToUse)}
                        disabled={
                            isOrderInProgress || isTotalsPending || (!isAgent && !isAgreeTerm)
                        }
                        data-gtm-id="begin-checkout"
                    >
                        {isTotalsPending ? (
                            <ApplyingDiscount>
                                <span>Applying Discount</span>
                                <ClipLoaderCommon color={theme.colors.main} size={22} margin={0} />
                            </ApplyingDiscount>
                        ) : isOrderInProgress ? (
                            <ClipLoaderCommon color={theme.colors.main} size={35} margin={5} />
                        ) : (
                            <div data-gtm-id="proceed-to-payment">
                                <img src="/images/CardPay.svg" alt="cardPay" />
                                <p>Proceed to Payment</p>
                            </div>
                        )}
                    </PaymentButton>
                </BottomContainer>
            </Container>
        </>
    );
};

const QuestionMarkHelper = styled.div`
    cursor: pointer;
`;

interface ContainerProps {
    dataPopupOpen?: boolean;
    $embedded?: boolean;
}

const Container = styled.div<ContainerProps>`
    width: 100%;
    border-radius: 8px;
    pointer-events: ${(props) => (props.dataPopupOpen ? "none" : "auto")};

    ${breakpoint.breakTablet`
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 24px;
        width: 100%;
        background-color: ${theme.colors.white};
        padding: 32px 24px;
    `}

    ${breakpoint.breakLarge`
        padding: 32px 40px;
    `}

    ${breakpoint.breakOnlyMobile`
        margin-bottom: var(--bottom-container-height);
    `}
    ${(props) =>
        props.$embedded &&
        `
        @media (max-width: 767px) {
            margin-bottom: 0;
        }
    `}
    .help-popup-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 5;
        pointer-events: auto;
    }

    .help-popup-content {
        background: ${(p) => p.theme.colors.white};
        padding: 2rem;
        border-radius: 8px;
        max-width: 400px;
        ${breakpoint.breakMobile`
            width: 330px;   
        `}
        p {
            text-align: center;
            line-height: 150%;
            font-family: poppins;
        }
        h3 {
            margin-bottom: 1rem;
        }

        button {
            margin-top: 1rem;
            padding: 8px 16px;
        }
    }

    ${breakpoint.breakOnlyMobile`
        display: block;
    `}
`;

const CommissionBanner = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 12px 16px;
    gap: 10px;
    background: #f0e9ff;
    border-radius: 8px;
    font-family: "Poppins";
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 120%;
    color: #131214;
    box-sizing: border-box;
    margin: 0 16px 16px 16px;
    ${breakpoint.breakTablet`
      
       margin: 0 0 16px 0;
    `}
`;

const ListOptions = styled.div`
    .MuiRadio-colorSecondary.Mui-checked {
        color: ${(props) => props.theme.colors.main} !important;
    }
    .MuiTypography-body1 {
        font-family: Poppins;
        font-size: 18px;
        font-weight: 500;
        line-height: 120%;

        ${breakpoint.breakTablet`
            font-size: 14px;
        `}
    }

    .PrivateSwitchBase-root-1 {
        padding: 9px 12px;
    }

    .checkBox {
        display: flex;
        align-items: center;
    }

    .checkBoxText {
        font-size: 13px;
        font-weight: normal;
        line-height: 18px;
        color: ${(p) => p.theme.colors.gray_2};
        position: relative;
        top: -5px;

        ${breakpoint.breakIpadPro`
            font-size: 14px;
        `}

        ${breakpoint.breakLarge`
            .checkBoxText {
                margin-left: 14px;
            }
        `}
    }
    a {
        color: ${(props) => props.theme.colors.highlight};
    }
`;

const PaymentButton = styled(StyledButton)`
    width: 100%;
    height: 48px;
    background: ${(p) => p.theme.gradients.button};
    border-radius: 4px;
    font-weight: 500;
    margin: 0;

    div {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 8px;

        p {
            font-weight: 500;
            font-size: 16px;
            line-height: 120%;
            color: ${(p) => p.theme.colors.black};
        }

        img {
            width: 24px;
            height: 24px;
        }
    }
`;

const SendLinkButton = styled(StyledBorderButton)`
    width: 100%;
    height: 48px;
    font-weight: 500;
    background: ${(p) => p.theme.colors.white};
    border: 1px solid ${(p) => p.theme.colors.highlight};
    border-radius: 4px;
    margin: 0;

    div {
        display: flex;
        justify-content: center;
        align-items: center;

        p {
            font-weight: 500;
            font-size: 16px;
            line-height: 120%;
            color: ${(p) => p.theme.colors.highlight};
        }
    }
`;

const StyledItemInfo = styled.div`
    background-color: ${theme.colors.items_background};
    padding: 3px 12px;
    border-radius: 50px;
    font-size: 14px;
    font-family: "Plus Jakarta Sans", sans-serif;
    font-weight: 600;
    color: ${theme.colors.items_color};
`;

const PriceBlockStyled = styled.div`
    font-family: Poppins;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 8px 12px;
    gap: 2px;
    background: #ffffff;
    /* Square along the bottom, flush to the column edges: the payment bar sits
       directly beneath and carries the matching lower corners, so the two read
       as one card. */
    border-radius: 8px 8px 0 0;
    margin: 0;
    box-sizing: border-box;

    ${breakpoint.breakTablet`
        padding: 12px 12px;
        gap: 24px;
        width: 100%;
        margin: 0;
        border: 1px solid #f4f6f7;
        box-shadow:
            0px 0px 1px rgba(20, 20, 20, 0.12),
            0px 1px 8px rgba(20, 20, 20, 0.08);
        border-radius: 16px;
    `}

    .title {
        font-weight: 600;
        font-size: 18px;
        line-height: 120%;
        color: #131214;
        margin: 0;
        padding: 0;
        width: 100%;
    }

    .inner-wrapper {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        padding: 0;
        gap: 2px;
        width: 100%;
        background: transparent;
        border: none;
        box-sizing: border-box;

        ${breakpoint.breakTablet`
            padding: 24px;
            gap: 24px;
            background: #f4f6f7;
            border: 1px solid #f4f6f7;
            border-radius: 8px;
        `}
    }

    .summary-card {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 4px;
        width: 100%;
        background: transparent;
        border-radius: 8px;
        box-sizing: border-box;

        ${breakpoint.breakTablet`
            padding: 16px;
            gap: 12px;
            background: #ffffff;
        `}
    }

    .summary-title {
        font-weight: 600;
        font-size: 16px;
        line-height: 120%;
        color: #131214;
        margin: 0;
        padding: 0;
        width: 100%;
        align-self: stretch;
        display: flex;
        align-items: center;
        gap: 12px;

        ${breakpoint.breakTablet`
            font-size: 18px;
        `}
    }

    .original-price-value-wrapper {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        padding: 0px;
        width: 100%;
        box-sizing: border-box;

        .price-label {
            font-weight: 400;
            font-size: 16px;
            line-height: 120%;
            color: #131214;
        }

        .price-original {
            font-weight: 500;
            font-size: 16px;
            line-height: 120%;
            color: #131214;
        }
    }

    .discount-row {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 4px;

        .discount-badge {
            font-weight: 500;
            font-size: 12px;
            padding: 3px 12px;
            background-color: ${theme.colors.points};
            color: ${theme.colors.points_text};
            border-radius: ${theme.borderRadius.large};
        }

        .loading-spinner {
            width: 16px;
            height: 16px;
            border: 2px solid ${theme.colors.gray_6_5};
            border-top: 2px solid ${theme.colors.purple};
            border-radius: 50%;
            animation: spin 1s linear infinite;
        }

        @keyframes spin {
            0% {
                transform: rotate(0deg);
            }
            100% {
                transform: rotate(360deg);
            }
        }
    }

    .checkbox-option {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        padding: 12px 8px;
        gap: 12px;
        width: 100%;
        background: #fcfcfc;
        border: 1px solid #e6e9eb;
        border-radius: 8px;
        box-sizing: border-box;

        ${breakpoint.breakTablet`
            padding: 12px 16px;
            background: #ffffff;
        `}

        .checkbox-row {
            display: flex;
            flex-direction: row;
            align-items: center;
            padding: 0px;
            gap: 6px;
            width: 100%;
        }

        .checkbox-label {
            font-weight: 500;
            font-size: 14px;
            line-height: 130%;
            color: #131214;
        }

        .price-discount-badge-wrapper {
            display: inline-flex;
            align-items: center;
        }

        .price-discount-badge {
            font-weight: 500;
            font-size: 12px;
            padding: 3px 12px;
            background-color: ${(p) => p.theme.colors.points};
            color: ${(p) => p.theme.colors.points_text};
            border-radius: ${(p) => p.theme.borderRadius.large};
        }

        .loading-spinner {
            width: 16px;
            height: 16px;
            border: 2px solid ${(p) => p.theme.colors.gray_6_5};
            border-top: 2px solid ${(p) => p.theme.colors.purple};
            border-radius: 50%;
            animation: spin 1s linear infinite;
        }

        @keyframes spin {
            0% {
                transform: rotate(0deg);
            }
            100% {
                transform: rotate(360deg);
            }
        }

        .points-available {
            display: flex;
            align-items: center;
            gap: 4px;
            width: 100%;

            span:first-child {
                font-weight: 400;
                font-size: 12px;
                line-height: 80%;
                color: #131214;
            }

            span:last-child {
                font-weight: 500;
                font-size: 16px;
                line-height: 120%;
                color: #318184;
            }
        }

        .points-summary-row {
            display: flex;
            gap: 12px;
            width: 100%;
            margin-bottom: 8px;

            .points-summary-item {
                flex: 1;
                display: flex;
                flex-direction: column;
                align-items: center;
                background: #f8f9fa;
                border-radius: 8px;
                padding: 8px 4px;
                gap: 4px;
            }

            .points-summary-label {
                font-size: 11px;
                font-weight: 400;
                color: #898d8f;
                text-transform: uppercase;
                letter-spacing: 0.5px;
            }

            .points-summary-value {
                font-size: 14px;
                font-weight: 600;
                color: #5c6bc0;

                &.available {
                    color: #00bfa5;
                }

                &.pending {
                    color: #ffa500;
                }
            }

            ${breakpoint.breakOnlyMobile`
                gap: 6px;

                .points-summary-item {
                    padding: 6px 2px;
                }

                .points-summary-label {
                    font-size: 10px;
                }

                .points-summary-value {
                    font-size: 12px;
                }
            `}
        }

        .slider-container {
            width: 100%;
            display: flex;
            flex-direction: column;
            gap: 4px;

            ${breakpoint.breakOnlyMobile`
                padding: 0 8px;
            `}
        }

        .slider-value-label {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 6px;

            span:first-child {
                font-weight: 500;
                font-size: 14px;
                line-height: 120%;
                color: #131214;
            }

            span:last-child {
                font-weight: 600;
                font-size: 14px;
                line-height: 120%;
                color: #31b4b9;
            }
        }
    }

    .checkbox-option-commission {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        padding: 14px 8px;
        gap: 12px;
        width: 100%;
        background: #fcfcfc;
        border: 1px solid #e6e9eb;
        border-radius: 8px;
        box-sizing: border-box;

        ${breakpoint.breakTablet`
            padding: 12px 16px;
            background: #ffffff;
        `}

        .checkbox-row {
            display: flex;
            flex-direction: row;
            align-items: center;
            padding: 0px;
            gap: 6px;
            width: 100%;
        }

        .checkbox-loading-spinner {
            width: 20px;
            height: 20px;
            border: 2px solid ${theme.colors.gray_6_5};
            border-top: 2px solid ${theme.colors.purple};
            border-radius: 50%;
            animation: spin 1s linear infinite;
        }

        .commission-label-wrapper {
            display: flex;
            flex-direction: column;
            gap: 8px;
        }

        .checkbox-label {
            font-weight: 500;
            font-size: 14px;
            line-height: 130%;
            color: #131214;
        }

        .price-discount-badge-wrapper {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            margin-left: 4px;
            min-width: 60px;
            min-height: 24px;
        }

        .price-discount-badge {
            font-weight: 500;
            font-size: 12px;
            padding: 3px 12px;
            background-color: ${theme.colors.points};
            color: ${theme.colors.points_text};
            border-radius: ${theme.borderRadius.large};
        }

        .loading-spinner {
            width: 16px;
            height: 16px;
            border: 2px solid ${theme.colors.gray_6_5};
            border-top: 2px solid ${theme.colors.purple};
            border-radius: 50%;
            animation: spin 1s linear infinite;
        }

        @keyframes spin {
            0% {
                transform: rotate(0deg);
            }
            100% {
                transform: rotate(360deg);
            }
        }

        .commission-hint {
            font-weight: 400;
            font-size: 12px;
            line-height: 120%;
            color: #6e7375;
        }

        .slider-container {
            width: 100%;
        }
    }

    .commission-info-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0;
        width: 100%;

        .info-label {
            font-weight: 400;
            font-size: 14px;
            line-height: 120%;
            color: #131214;
        }

        .info-value {
            font-weight: 500;
            font-size: 14px;
            line-height: 130%;
            color: ${theme.colors.highlight};
            text-align: right;
        }
    }

    .slider-wrapper {
        margin-top: -4px;
    }

    .price-value-wrapper {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: flex-end;
        padding: 0px;
        width: 100%;
        box-sizing: border-box;

        .price-label-pay {
            font-weight: 500;
            font-size: 18px;
            line-height: 120%;
            color: #131214;
        }

        .price-discounted-value {
            font-weight: 600;
            font-size: 18px;
            line-height: 120%;
            color: ${theme.colors.price || theme.colors.highlight};
            display: flex;
            align-items: center;
            gap: 8px;
        }

        .price-original-strikethrough {
            position: relative;
            font-weight: 400;
            font-size: 16px;
            color: ${theme.colors.gray_3};
            ${theme.colors.price
                ? `
                text-decoration: none;
                &::after {
                    content: '';
                    position: absolute;
                    left: -2px;
                    right: -2px;
                    top: 0;
                    bottom: 0;
                    background: linear-gradient(
                        to top left,
                        transparent calc(50% - 1px),
                        ${theme.colors.price} calc(50% - 1px),
                        ${theme.colors.price} calc(50% + 1px),
                        transparent calc(50% + 1px)
                    );
                }
            `
                : `text-decoration: line-through;`}
        }

        .new-price-box {
            display: flex;
            justify-content: center;
            align-items: center;
            padding: 6px 7px;
            background: #fbfdfe;
            border: 1px solid #e6e9eb;
            border-radius: 8px;
            font-weight: 600;
            font-size: 18px;
            line-height: 120%;
            background: linear-gradient(0deg, #31b4b9, #31b4b9);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;

            &[data-max="true"] {
                background: linear-gradient(0deg, #ff9152, #ff9152);
                -webkit-background-clip: text;
                -webkit-text-fill-color: transparent;
                background-clip: text;
                border-color: #ff9152;
            }
        }
    }

    .action-section {
        display: none;

        ${breakpoint.breakTablet`
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 0;
            gap: 16px;
            width: 100%;
            background: transparent;
            box-sizing: border-box;
        `}

        .terms-text {
            font-weight: 400;
            font-size: 14px;
            line-height: 150%;
            color: #6e7375;
            text-align: left;
            width: 100%;
            padding: 0 6px;

            /* Reads as part of the sentence rather than a coloured callout; the
               underline is what marks it as a link. */
            a {
                color: inherit;
                text-decoration: underline;
            }
        }
    }
`;

const ViewSentEmailsButton = styled.button`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    padding: 0px;
    gap: 2px;
    width: 100%;
    height: 18px;
    background: none;
    border: none;
    cursor: pointer;

    span {
        font-family: "Poppins";
        font-weight: 500;
        font-size: 12px;
        line-height: 120%;
        color: ${theme.colors.highlight};
    }

    svg {
        width: 18px;
        height: 18px;
    }

    ${breakpoint.breakTablet`
        transition: opacity 0.2s ease;
        &:hover {
            opacity: 0.7;
        }
    `}
`;

const EditablePriceBox = styled.div<{ "data-max"?: boolean }>`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 6px 7px;
    background: #fbfdfe;
    border: 1px solid #e6e9eb;
    border-radius: 8px;
    font-weight: 600;
    font-size: 18px;
    line-height: 120%;
    gap: 4px;

    span {
        background: linear-gradient(0deg, ${theme.colors.highlight}, ${theme.colors.highlight});
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
    }

    img {
        cursor: pointer;
        opacity: 0.7;
        transition: opacity 0.2s;

        &:hover {
            opacity: 1;
        }
    }

    input {
        width: 80px;
        border: none;
        background: transparent;
        font-weight: 600;
        font-size: 18px;
        line-height: 120%;
        background: linear-gradient(0deg, ${theme.colors.highlight}, ${theme.colors.highlight});
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        outline: none;
        padding: 0;
        text-align: center;

        &::-webkit-inner-spin-button,
        &::-webkit-outer-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }

        &[type="number"] {
            -moz-appearance: textfield;
        }
    }

    &[data-max="true"] {
        border-color: #ff9152;

        span,
        input {
            background: linear-gradient(0deg, #ff9152, #ff9152);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
        }
    }
`;

const BottomContainer = styled.div<{ $embedded?: boolean }>`
    display: flex;
    flex-direction: column;
    padding: 8px 12px;
    background-color: ${theme.colors.white};
    gap: 8px;
    /* Completes the summary card above it. */
    border-radius: 0 0 8px 8px;

    ${breakpoint.breakTablet`
        display: none;
    `}

    ${breakpoint.breakOnlyMobile`
        position: fixed;
        width: 100%;
        left: 0;
        bottom: 0px;
        box-sizing: border-box;
        z-index: 60;
        padding: 8px 12px;
    `}
    ${(props) =>
        props.$embedded &&
        `
        @media (max-width: 767px) {
            position: relative;
            left: auto;
            bottom: auto;
            z-index: auto;
            padding: 8px 12px;
        }
    `};
`;

interface IProps {
    items: ICartItemData[];
    customers?: ICustomerDetail[];
    quoteId?: string;
    showSendLinkPopup?: boolean;
    handleOrderCreate: (
        paymentType: string,
        pointsApplied: number,
        commissionApplied?: number | null,
    ) => Promise<void>;
    onOrderCreateSuccess?: () => void;
    isQuotesPage?: boolean;
}

export const CheckoutStep3 = (props: IProps) => {
    return (
        <MainContainer data-popup-open={props.showSendLinkPopup}>
            <PaymentOptionComponent {...props} />
        </MainContainer>
    );
};
interface MainContainerProps {
    "data-popup-open"?: boolean;
}

const MainContainer = styled.div<MainContainerProps>`
    display: flex;
    flex-direction: column;
    font-family: Poppins;
    min-height: 60vh;
    position: relative;
`;

const ApplyingDiscount = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;

    span {
        font-size: 15px;
        font-weight: 600;
        color: ${theme.colors.black};
    }
`;
